import { useState, useEffect } from 'react';
import './ReceiverDetail.css';
import LocationPicker from './LocationPicker';
import SetupInstructions from './SetupInstructions';
import timezones from '../utils/timezones';

function AddReceiver({ onSave, onCancel, apiErrors = [], savedReceiver = null }) {
  // Generate a UUID that will be sent to the backend as the receiver's ID
  const [previewUuid, setPreviewUuid] = useState('');

  useEffect(() => {
    setPreviewUuid(crypto.randomUUID());
  }, []);

  const [formData, setFormData] = useState({
    receiverName: '',
    ipAddress: '',
    latitude: '',
    longitude: '',
    antennaLocation: '',
    receiverType: '',
    softwareVersion: '',
    timezone: 'UTC',
    airportIcao: '',
    notes: '',
    distanceUnits: 'metric',
    rangeRingsNumber: '5',
    rangeRingsInterval: '50',
    maxDistance: '250',
    antennaHeightAboveGround: '',
    upInTheAirCode: ''
  });

  const [errors, setErrors] = useState({});
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uuidCopied, setUuidCopied] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleLocationChange = (lat, lng) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString()
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.receiverName.trim()) {
      newErrors.receiverName = 'Receiver name is required';
    }

    const lat = parseFloat(formData.latitude);
    if (!formData.latitude || isNaN(lat) || lat < -90 || lat > 90) {
      newErrors.latitude = 'Valid latitude (-90 to 90) is required';
    }

    const lon = parseFloat(formData.longitude);
    if (!formData.longitude || isNaN(lon) || lon < -180 || lon > 180) {
      newErrors.longitude = 'Valid longitude (-180 to 180) is required';
    }

    if (formData.upInTheAirCode && formData.upInTheAirCode.length !== 8) {
      newErrors.upInTheAirCode = 'UpInTheAir code must be exactly 8 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCopyUuid = () => {
    const uuidToCopy = savedReceiver?.receiverUuid || previewUuid;
    navigator.clipboard.writeText(uuidToCopy).then(() => {
      setUuidCopied(true);
      setTimeout(() => setUuidCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy UUID:', err);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate() && !isSubmitting) {
      setIsSubmitting(true);

      // Build data object with only non-empty fields
      const data = {
        receiverUuid: previewUuid,
        receiverName: formData.receiverName,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        timezone: formData.timezone || 'UTC'
      };

      // Add optional fields only if they have values
      if (formData.ipAddress) data.ipAddress = formData.ipAddress;
      if (formData.antennaLocation) data.antennaLocation = formData.antennaLocation;
      if (formData.receiverType) data.receiverType = formData.receiverType;
      if (formData.softwareVersion) data.softwareVersion = formData.softwareVersion;
      if (formData.airportIcao) data.airportIcao = formData.airportIcao;
      if (formData.notes) data.notes = formData.notes;
      if (formData.distanceUnits) data.distanceUnits = formData.distanceUnits;
      if (formData.rangeRingsNumber) data.rangeRingsNumber = parseInt(formData.rangeRingsNumber);
      if (formData.rangeRingsInterval) data.rangeRingsInterval = parseInt(formData.rangeRingsInterval);
      if (formData.maxDistance) data.maxDistance = parseInt(formData.maxDistance);
      if (formData.antennaHeightAboveGround) data.antennaHeightAboveGround = parseFloat(formData.antennaHeightAboveGround);
      if (formData.upInTheAirCode) data.upInTheAirCode = formData.upInTheAirCode;

      await onSave(data);
      // Note: If there's an error, apiErrors prop will be updated and component will re-render
      // If successful, savedReceiver prop will be set
    }
  };

  // Reset submitting state when apiErrors change (indicates an error occurred)
  useEffect(() => {
    if (apiErrors.length > 0) {
      setIsSubmitting(false);
    }
  }, [apiErrors]);

  // Get the UUID to display (saved or preview)
  const displayUuid = savedReceiver?.receiverUuid || previewUuid;

  // The platform-specific body lives in <SetupInstructions> so AddReceiver
  // and ReceiverDetail can't drift. Same component is rendered on both
  // pages; keep that file in sync with the Downloads page tabs.
  const renderInstructions = () => (
    <SetupInstructions software={formData.softwareVersion} uuid={displayUuid} />
  );

  return (
    <div className="receiver-detail">
      <form onSubmit={handleSubmit}>
        <div className="detail-header">
          <div className={`receiver-status-large ${savedReceiver ? 'online' : 'pending'}`}>
            {savedReceiver ? 'RECEIVER ADDED' : 'NEW RECEIVER'}
          </div>
        </div>

        {/* Display success message */}
        {savedReceiver && (
          <div className="api-success-banner">
            <h4>Receiver added successfully! Configure your receiver software using the instructions below.</h4>
          </div>
        )}

        {/* Display API errors */}
        {apiErrors.length > 0 && (
          <div className="api-error-banner">
            <h4>Failed to add receiver</h4>
            <ul>
              {apiErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="detail-sections">
          <div className="detail-section">
            <h3>Basic Information</h3>
            <div className="detail-grid">
              <div className="detail-field">
                <label>Receiver UUID</label>
                <div className="uuid-field-container">
                  <input
                    type="text"
                    value={displayUuid}
                    disabled
                    className="field-input disabled uuid-input"
                  />
                  <button
                    type="button"
                    className="btn-copy-uuid"
                    onClick={handleCopyUuid}
                    title="Copy UUID"
                  >
                    {uuidCopied ? (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 2a2 2 0 0 1 2-2h4.586a1 1 0 0 1 .707.293l2.414 2.414a1 1 0 0 1 .293.707V11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2z" stroke="currentColor" strokeWidth="1" fill="none"/>
                        <path d="M2 6h2v7a1 1 0 0 0 1 1h5v2H5a2 2 0 0 1-2-2V6z" fill="currentColor"/>
                      </svg>
                    )}
                  </button>
                </div>
                <span className="field-note">Use this UUID to configure your receiver software</span>
              </div>
              <div className="detail-field">
                <label>Receiver Name *</label>
                <input
                  type="text"
                  name="receiverName"
                  value={formData.receiverName}
                  onChange={handleChange}
                  placeholder="e.g., Home Station, London Receiver"
                  className={`field-input ${errors.receiverName ? 'error' : ''}`}
                  disabled={!!savedReceiver}
                />
                {errors.receiverName && <span className="error-message">{errors.receiverName}</span>}
              </div>
              <div className="detail-field">
                <label>IP Address</label>
                <input
                  type="text"
                  value="Will be auto-detected"
                  disabled
                  className="field-input disabled"
                />
                <span className="field-note">IP address will be automatically detected when your receiver connects</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Location</h3>
            <div className="detail-grid">
              <div className="detail-field">
                <label>Latitude *</label>
                <input
                  type="number"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  placeholder="51.5074"
                  step="0.0001"
                  className={`field-input ${errors.latitude ? 'error' : ''}`}
                  disabled={!!savedReceiver}
                />
                {errors.latitude && <span className="error-message">{errors.latitude}</span>}
              </div>
              <div className="detail-field">
                <label>Longitude *</label>
                <input
                  type="number"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  placeholder="-0.1278"
                  step="0.0001"
                  className={`field-input ${errors.longitude ? 'error' : ''}`}
                  disabled={!!savedReceiver}
                />
                {errors.longitude && <span className="error-message">{errors.longitude}</span>}
              </div>
              <div className="detail-field">
                <label>Nearest Airport (ICAO)</label>
                <input
                  type="text"
                  name="airportIcao"
                  value={formData.airportIcao}
                  onChange={handleChange}
                  placeholder="e.g., KJFK, EGLL (optional)"
                  maxLength="4"
                  className="field-input"
                  disabled={!!savedReceiver}
                />
                <span className="field-note">Optional - can be auto-detected based on your location</span>
              </div>
            </div>
            {!savedReceiver && (
              <>
                <button
                  type="button"
                  onClick={() => setShowLocationPicker(true)}
                  className="btn-location-full"
                >
                  Pick Location on Map
                </button>

                {showLocationPicker && (
                  <div className="location-picker-container">
                    <LocationPicker
                      initialLat={formData.latitude ? parseFloat(formData.latitude) : null}
                      initialLon={formData.longitude ? parseFloat(formData.longitude) : null}
                      onLocationChange={handleLocationChange}
                      onClose={() => setShowLocationPicker(false)}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <div className="detail-section">
            <h3>Hardware</h3>
            <div className="detail-grid">
              <div className="detail-field">
                <label>Receiver Hardware</label>
                <select
                  name="receiverType"
                  value={formData.receiverType}
                  onChange={handleChange}
                  className="field-input"
                  disabled={!!savedReceiver}
                >
                  <option value="">Select hardware…</option>
                  <option value="RTL-SDR">RTL-SDR</option>
                  <option value="Airspy R2">Airspy R2</option>
                  <option value="Airspy Mini">Airspy Mini</option>
                  <option value="GNS Hulc">GNS Hulc</option>
                  <option value="Mode-S Beast">Mode-S Beast</option>
                  <option value="ADSBee 1090">ADSBee 1090</option>
                  <option value="Beast-In (TCP)">Beast-In (TCP from external decoder)</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="detail-field">
                <label>Antenna Location</label>
                <select
                  name="antennaLocation"
                  value={formData.antennaLocation}
                  onChange={handleChange}
                  className="field-input"
                  disabled={!!savedReceiver}
                >
                  <option value="">Select location...</option>
                  <option value="indoor">Indoor</option>
                  <option value="attic">Attic</option>
                  <option value="outdoor-roof">Outdoor (Roof)</option>
                  <option value="outdoor-balcony">Outdoor (Balcony)</option>
                  <option value="outdoor-other">Outdoor (Other)</option>
                </select>
              </div>
              <div className="detail-field">
                <label>Antenna Height Above Ground (meters)</label>
                <input
                  type="number"
                  name="antennaHeightAboveGround"
                  value={formData.antennaHeightAboveGround}
                  onChange={handleChange}
                  step="0.1"
                  placeholder="12"
                  className="field-input"
                  disabled={!!savedReceiver}
                />
              </div>
              <div className="detail-field">
                <label>Receiver Software</label>
                <select
                  name="softwareVersion"
                  value={formData.softwareVersion}
                  onChange={handleChange}
                  className="field-input"
                  disabled={!!savedReceiver}
                >
                  <option value="">Select software...</option>
                  <option value="aviotrack-windows">Aviotrack Windows Receiver</option>
                  <option value="aviotrack-mac">Aviotrack macOS Receiver</option>
                  <option value="aviotrack-linux">Aviotrack Linux Receiver</option>
                  <option value="readsb">readsb</option>
                  <option value="dump1090">dump1090</option>
                  <option value="dump1090-fa">dump1090-fa</option>
                </select>
              </div>
            </div>

            {/* Show instructions when software is selected */}
            {renderInstructions()}
          </div>

          <div className="detail-section">
            <h3>Configuration</h3>
            <div className="detail-grid">
              <div className="detail-field">
                <label>Timezone *</label>
                <select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                  className="field-input"
                  disabled={!!savedReceiver}
                >
                  {timezones.map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
              <div className="detail-field">
                <label>Distance Units</label>
                <select
                  name="distanceUnits"
                  value={formData.distanceUnits}
                  onChange={handleChange}
                  className="field-input"
                  disabled={!!savedReceiver}
                >
                  <option value="metric">Metric (km)</option>
                  <option value="aeronautical">Aeronautical (NM)</option>
                </select>
              </div>
              <div className="detail-field">
                <label>Range Rings</label>
                <input
                  type="number"
                  name="rangeRingsNumber"
                  value={formData.rangeRingsNumber}
                  onChange={handleChange}
                  min="0"
                  max="20"
                  className="field-input"
                  disabled={!!savedReceiver}
                />
              </div>
              <div className="detail-field">
                <label>Ring Interval</label>
                <input
                  type="number"
                  name="rangeRingsInterval"
                  value={formData.rangeRingsInterval}
                  onChange={handleChange}
                  min="1"
                  className="field-input"
                  disabled={!!savedReceiver}
                />
              </div>
              <div className="detail-field">
                <label>Max Distance</label>
                <input
                  type="number"
                  name="maxDistance"
                  value={formData.maxDistance}
                  onChange={handleChange}
                  min="1"
                  className="field-input"
                  disabled={!!savedReceiver}
                />
              </div>
              <div className="detail-field">
                <label>UpInTheAir Code</label>
                <input
                  type="text"
                  name="upInTheAirCode"
                  value={formData.upInTheAirCode}
                  onChange={handleChange}
                  maxLength="8"
                  placeholder="8-digit code (optional)"
                  className={`field-input ${errors.upInTheAirCode ? 'error' : ''}`}
                  disabled={!!savedReceiver}
                />
                {errors.upInTheAirCode && <span className="error-message">{errors.upInTheAirCode}</span>}
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Notes</h3>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              placeholder="Additional information about this receiver..."
              className="field-textarea"
              disabled={!!savedReceiver}
            />
          </div>

          {/* Action buttons at the bottom */}
          <div className="form-actions-bottom">
            {savedReceiver ? (
              <button type="button" onClick={onCancel} className="btn-save-detail">
                Done
              </button>
            ) : (
              <>
                <button type="button" onClick={onCancel} className="btn-cancel-detail">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save-detail"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Adding...' : 'Add Receiver'}
                </button>
              </>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

export default AddReceiver;
